// features/inventory/pages/MovementAnalyticsPage.tsx
/**
 * Movement Analytics Page — Stock velocity analysis with fast/slow movers and dead stock.
 *
 * Route: /inventory/analytics
 *
 * Features:
 * - Fast movers: products with highest sales velocity
 * - Slow movers: products with lowest movement
 * - Dead stock: products with zero movement in 90+ days
 * - Time period selector (7d, 30d, 90d, 12m)
 * - Summary stats: total movements, inflow, outflow, net change
 * - Branch filter
 * - Product detail navigation
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Pagination } from '@/components/ui/Pagination';
import { getStockTransactions, StockTransaction } from '@/features/stock/services/stockApi';
import { getStockLevels } from '@/features/stock/services/stockApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import {
    ArrowLeft,
    TrendingUp,
    TrendingDown,
    Package,
    RefreshCw,
    Zap,
    Snail,
    Skull,
    BarChart3,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

type TimePeriod = '7d' | '30d' | '90d' | '12m';
type AnalysisTab = 'fast' | 'slow' | 'dead';

interface MovementEntry {
    productId: string;
    productName: string;
    sku: string;
    branchName: string;
    totalInflow: number;
    totalOutflow: number;
    netChange: number;
    transactionCount: number;
    lastMovementDate: string | null;
    daysSinceLastMovement: number;
    velocity: number;
    status: 'fast' | 'normal' | 'slow' | 'dead';
}

// ============================================================
// CONSTANTS
// ============================================================

const PAGE_SIZE = 30;

const TIME_PERIODS: Array<{ value: TimePeriod; label: string; days: number }> = [
    { value: '7d', label: 'Last 7 Days', days: 7 },
    { value: '30d', label: 'Last 30 Days', days: 30 },
    { value: '90d', label: 'Last 90 Days', days: 90 },
    { value: '12m', label: 'Last 12 Months', days: 365 },
];

const TABS: Array<{
    value: AnalysisTab;
    label: string;
    icon: React.ElementType;
    desc: string;
}> = [
        {
            value: 'fast',
            label: 'Fast Movers',
            icon: Zap,
            desc: 'Highest sales velocity',
        },
        {
            value: 'slow',
            label: 'Slow Movers',
            icon: Snail,
            desc: 'Lowest movement',
        },
        {
            value: 'dead',
            label: 'Dead Stock',
            icon: Skull,
            desc: 'No movement in 90+ days',
        },
    ];

// ============================================================
// MAIN COMPONENT
// ============================================================

export function MovementAnalyticsPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const selectedBranchId = useAuthStore((s) => s.selectedBranchId);
    const addToast = useToastStore((s) => s.addToast);

    // State
    const [branchId, setBranchId] = useState(selectedBranchId || '');
    const [timePeriod, setTimePeriod] = useState<TimePeriod>('30d');
    const [activeTab, setActiveTab] = useState<AnalysisTab>('fast');
    const [page, setPage] = useState(1);

    // Queries
    const { data: branches } = useQuery({
        queryKey: ['branches-analytics', user?.tenantId],
        queryFn: async () => {
            try {
                const data = await getBranches(user!.tenantId);
                return Array.isArray(data) ? data : [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
    });

    // Fetch transactions for the selected period
    const {
        data: txnResponse,
        isLoading: txnLoading,
    } = useQuery({
        queryKey: [
            'transactions-analytics',
            user?.tenantId,
            branchId,
            timePeriod,
        ],
        queryFn: async () => {
            const periodDays =
                TIME_PERIODS.find((p) => p.value === timePeriod)
                    ?.days || 30;
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - periodDays);

            const allData: StockTransaction[] = [];
            let currentPage = 1;
            let hasMore = true;

            while (hasMore && currentPage <= 20) {
                const response = await getStockTransactions(
                    user!.tenantId,
                    {
                        limit: 100,
                        page: currentPage,
                        branchId: branchId || undefined,
                        startDate: startDate.toISOString(),
                    }
                );
                const data = response?.data || [];
                allData.push(...data);
                hasMore = data.length === 100;
                currentPage++;
            }

            return allData;
        },
        enabled: !!user?.tenantId,
        staleTime: 5 * 60 * 1000,
    });

    // Fetch current stock levels for dead stock detection
    const { data: stockResponse } = useQuery({
        queryKey: [
            'stock-levels-analytics',
            user?.tenantId,
            branchId,
        ],
        queryFn: async () => {
            const response = await getStockLevels(
                user!.tenantId,
                {
                    limit: 500,
                    branchId: branchId || undefined,
                    sortBy: 'quantityOnHand',
                    order: 'desc',
                }
            );
            return response?.data || [];
        },
        enabled: !!user?.tenantId,
    });

    // Derived data
    const branchesList = (branches || []) as Array<{
        id: string;
        name: string;
    }>;
    const transactions = (txnResponse || []) as StockTransaction[];
    const stockItems = Array.isArray(stockResponse)
        ? stockResponse
        : [];

    const periodDays =
        TIME_PERIODS.find((p) => p.value === timePeriod)?.days ||
        30;

    // Build movement entries
    // StockTransaction has: id, productId, productName, transactionType, quantity,
    // unitCost, batchNumber, performerName, branchName, notes, createdAt
    // It does NOT have branchId directly — we use branchName as the grouping key
    const movementEntries: MovementEntry[] = useMemo(() => {
        const productMap = new Map<string, MovementEntry>();

        // Process transactions — group by productId + branchName
        transactions.forEach((txn) => {
            if (!txn.productId) return;

            // Use branchName as the grouping key since branchId is not on StockTransaction
            const branchKey = txn.branchName || 'Unknown';
            const key = `${txn.productId}-${branchKey}`;
            const existing = productMap.get(key);
            const qty = parseFloat(txn.quantity || '0');

            if (!existing) {
                productMap.set(key, {
                    productId: txn.productId,
                    productName:
                        txn.productName || 'Unknown',
                    sku: '',
                    branchName: branchKey,
                    totalInflow: qty > 0 ? qty : 0,
                    totalOutflow:
                        qty < 0 ? Math.abs(qty) : 0,
                    netChange: qty,
                    transactionCount: 1,
                    lastMovementDate: txn.createdAt,
                    daysSinceLastMovement: 0,
                    velocity: 0,
                    status: 'normal',
                });
            } else {
                if (qty > 0) existing.totalInflow += qty;
                else
                    existing.totalOutflow += Math.abs(
                        qty
                    );
                existing.netChange += qty;
                existing.transactionCount++;
                if (
                    txn.createdAt >
                    (existing.lastMovementDate || '')
                ) {
                    existing.lastMovementDate =
                        txn.createdAt;
                }
            }
        });

        // Calculate velocity and status
        const now = new Date();
        const entries = Array.from(productMap.values()).map(
            (entry) => {
                const velocity =
                    entry.totalOutflow / periodDays;
                const daysSinceLastMovement =
                    entry.lastMovementDate
                        ? Math.ceil(
                            (now.getTime() -
                                new Date(
                                    entry.lastMovementDate
                                ).getTime()) /
                            (1000 * 60 * 60 * 24)
                        )
                        : 999;

                let status: MovementEntry['status'] =
                    'normal';
                if (daysSinceLastMovement >= 90)
                    status = 'dead';
                else if (
                    velocity < 0.1 &&
                    entry.totalOutflow === 0
                )
                    status = 'slow';
                else if (velocity >= 1) status = 'fast';

                return {
                    ...entry,
                    velocity,
                    daysSinceLastMovement,
                    status,
                };
            }
        );

        // Add products from stock levels that have no transactions
        const transactedKeys = new Set(
            entries.map(
                (e) => `${e.productId}-${e.branchName}`
            )
        );

        stockItems.forEach((item: any) => {
            const branchKey =
                item.branchName || 'Unknown';
            const key = `${item.productId}-${branchKey}`;
            if (
                !transactedKeys.has(key) &&
                parseFloat(item.quantityOnHand || '0') > 0
            ) {
                entries.push({
                    productId: item.productId,
                    productName:
                        item.productName || 'Unknown',
                    sku: '',
                    branchName: branchKey,
                    totalInflow: 0,
                    totalOutflow: 0,
                    netChange: 0,
                    transactionCount: 0,
                    lastMovementDate: null,
                    daysSinceLastMovement: 999,
                    velocity: 0,
                    status: 'dead',
                });
            }
        });

        return entries;
    }, [transactions, stockItems, periodDays]);

    // Filter based on active tab
    const filteredEntries = useMemo(() => {
        switch (activeTab) {
            case 'fast':
                return movementEntries
                    .filter(
                        (e) =>
                            e.status === 'fast' &&
                            e.totalOutflow > 0
                    )
                    .sort(
                        (a, b) =>
                            b.totalOutflow - a.totalOutflow
                    );
            case 'slow':
                return movementEntries
                    .filter(
                        (e) =>
                            e.status === 'slow' ||
                            (e.velocity < 0.5 &&
                                e.totalOutflow > 0)
                    )
                    .sort(
                        (a, b) => a.velocity - b.velocity
                    );
            case 'dead':
                return movementEntries
                    .filter((e) => e.status === 'dead')
                    .sort(
                        (a, b) =>
                            b.daysSinceLastMovement -
                            a.daysSinceLastMovement
                    );
            default:
                return [];
        }
    }, [movementEntries, activeTab]);

    // Stats
    const stats = useMemo(() => {
        const totalInflow = movementEntries.reduce(
            (sum, e) => sum + e.totalInflow,
            0
        );
        const totalOutflow = movementEntries.reduce(
            (sum, e) => sum + e.totalOutflow,
            0
        );
        const fastCount = movementEntries.filter(
            (e) => e.status === 'fast'
        ).length;
        const slowCount = movementEntries.filter(
            (e) => e.status === 'slow'
        ).length;
        const deadCount = movementEntries.filter(
            (e) => e.status === 'dead'
        ).length;
        const totalProducts = movementEntries.length;

        return {
            totalInflow,
            totalOutflow,
            netChange: totalInflow - totalOutflow,
            fastCount,
            slowCount,
            deadCount,
            totalProducts,
            turnoverRate:
                totalOutflow > 0 && totalProducts > 0
                    ? (
                        totalOutflow / totalProducts
                    ).toFixed(2)
                    : '0',
        };
    }, [movementEntries]);

    // Pagination
    const totalCount = filteredEntries.length;
    const totalPages = Math.max(
        1,
        Math.ceil(totalCount / PAGE_SIZE)
    );
    const paginatedEntries = filteredEntries.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
    );

    const getBranchName = (id: string) =>
        branchesList.find((b) => b.id === id)?.name ||
        'Warehouse';

    const formatNumber = (val: number): string => {
        return val.toLocaleString(undefined, {
            maximumFractionDigits: 1,
        });
    };

    const formatDate = (dateStr: string | null): string => {
        if (!dateStr) return 'Never';
        return new Date(dateStr).toLocaleDateString(
            'en-NG',
            {
                day: 'numeric',
                month: 'short',
            }
        );
    };

    const handlePageChange = useCallback(
        (newPage: number) => {
            if (
                newPage < 1 ||
                newPage > totalPages ||
                newPage === page
            )
                return;
            setPage(newPage);
        },
        [totalPages, page]
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() =>
                                    navigate('/inventory')
                                }
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg shadow-blue-500/25">
                                <BarChart3 className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    Movement Analytics
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {getBranchName(
                                        branchId
                                    )}{' '}
                                    ·{' '}
                                    {
                                        TIME_PERIODS.find(
                                            (p) =>
                                                p.value ===
                                                timePeriod
                                        )?.label
                                    }{' '}
                                    ·{' '}
                                    {stats.totalProducts.toLocaleString()}{' '}
                                    products
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate(0)}
                            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                            type="button"
                        >
                            <RefreshCw className="w-4 h-4" />{' '}
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                {/* ========== DESCRIPTIVE ANALYTICS STATS ========== */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {/* Total Products Analyzed */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                        <div className="flex items-center justify-between mb-1">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-slate-500 to-slate-700"><Package className="w-4 h-4 text-white" /></div>
                            <span className="text-[10px] text-slate-400 font-medium">{TIME_PERIODS.find(p => p.value === timePeriod)?.label.replace('Last ', '')}</span>
                        </div>
                        <p className="text-2xl font-bold text-slate-900">{stats.totalProducts.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Products with transaction history</p>
                    </motion.div>

                    {/* Stock In */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-4">
                        <div className="flex items-center justify-between mb-1">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600"><TrendingUp className="w-4 h-4 text-white" /></div>
                            <span className="text-[10px] text-emerald-600 font-semibold">{stats.totalOutflow > 0 ? `${Math.round((stats.totalInflow / stats.totalOutflow) * 100)}% vs out` : '—'}</span>
                        </div>
                        <p className="text-2xl font-bold text-emerald-700">{formatNumber(stats.totalInflow)}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Units received/purchased</p>
                    </motion.div>

                    {/* Stock Out */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-rose-200 shadow-sm p-4">
                        <div className="flex items-center justify-between mb-1">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-rose-500 to-pink-600"><TrendingDown className="w-4 h-4 text-white" /></div>
                            <span className="text-[10px] text-rose-600 font-semibold">{stats.totalInflow > 0 ? `${Math.round((stats.totalOutflow / stats.totalInflow) * 100)}% vs in` : '—'}</span>
                        </div>
                        <p className="text-2xl font-bold text-rose-700">{formatNumber(stats.totalOutflow)}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Units sold/transferred out</p>
                    </motion.div>

                    {/* Net Change */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className={clsx('bg-white rounded-2xl border p-4', stats.netChange >= 0 ? 'border-emerald-200' : 'border-rose-200')}>
                        <div className="flex items-center justify-between mb-1">
                            <div className={clsx('p-2 rounded-lg bg-gradient-to-br', stats.netChange >= 0 ? 'from-emerald-500 to-teal-600' : 'from-rose-500 to-pink-600')}>
                                {stats.netChange >= 0 ? <TrendingUp className="w-4 h-4 text-white" /> : <TrendingDown className="w-4 h-4 text-white" />}
                            </div>
                            <span className="text-[10px] text-slate-400 font-medium">Net movement</span>
                        </div>
                        <p className={clsx('text-2xl font-bold', stats.netChange >= 0 ? 'text-emerald-700' : 'text-rose-700')}>{stats.netChange >= 0 ? '+' : ''}{formatNumber(stats.netChange)}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{stats.netChange > 0 ? 'More coming in than going out' : stats.netChange < 0 ? 'More going out than coming in' : 'Balanced inflow/outflow'}</p>
                    </motion.div>

                    {/* Turnover Rate */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-indigo-200 shadow-sm p-4">
                        <div className="flex items-center justify-between mb-1">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600"><BarChart3 className="w-4 h-4 text-white" /></div>
                            <span className="text-[10px] text-indigo-600 font-semibold">per {TIME_PERIODS.find(p => p.value === timePeriod)?.label.replace('Last ', '').toLowerCase()}</span>
                        </div>
                        <p className="text-2xl font-bold text-indigo-700">{stats.turnoverRate}x</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{parseFloat(stats.turnoverRate) > 1 ? 'Healthy — stock is moving fast' : parseFloat(stats.turnoverRate) > 0.5 ? 'Moderate turnover' : 'Slow — consider promotions'}</p>
                    </motion.div>

                    {/* Fast vs Dead */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                        <div className="flex items-center justify-between mb-1">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600"><Zap className="w-4 h-4 text-white" /></div>
                            <span className="text-[10px] text-slate-400 font-medium">Fast vs Dead</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                            <p className="text-2xl font-bold text-blue-700">{stats.fastCount}</p>
                            <span className="text-slate-300">vs</span>
                            <p className="text-2xl font-bold text-rose-700">{stats.deadCount}</p>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-0.5">{stats.fastCount > stats.deadCount ? `${stats.fastCount - stats.deadCount} more fast movers` : stats.deadCount > stats.fastCount ? `${stats.deadCount - stats.fastCount} more dead stock` : 'Equal fast movers & dead stock'}</p>
                    </motion.div>
                </div>

                {/* Time Period & Branch Filters */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5">
                    <div className="flex flex-col lg:flex-row gap-1.5">
                        <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                            {TIME_PERIODS.map((period) => (
                                <button
                                    key={period.value}
                                    onClick={() => {
                                        setTimePeriod(
                                            period.value
                                        );
                                        setPage(1);
                                    }}
                                    className={clsx(
                                        'px-3 py-2 rounded-lg text-xs font-medium transition-all',
                                        timePeriod ===
                                            period.value
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                    )}
                                    type="button"
                                >
                                    {period.label}
                                </button>
                            ))}
                        </div>
                        {branchesList.length > 1 && (
                            <select
                                value={branchId}
                                onChange={(e) => {
                                    setBranchId(
                                        e.target.value
                                    );
                                    setPage(1);
                                }}
                                className="px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                            >
                                <option value="">
                                    All Branches
                                </option>
                                {branchesList.map((b) => (
                                    <option
                                        key={b.id}
                                        value={b.id}
                                    >
                                        {b.name}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>

                {/* Analysis Tabs */}
                <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-0.5 w-fit">
                    {TABS.map((tab) => (
                        <button
                            key={tab.value}
                            onClick={() => {
                                setActiveTab(tab.value);
                                setPage(1);
                            }}
                            className={clsx(
                                'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
                                activeTab === tab.value
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700'
                            )}
                            type="button"
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                {txnLoading ? (
                    <div className="flex justify-center py-20">
                        <Spinner size="xl" />
                    </div>
                ) : paginatedEntries.length === 0 ? (
                    <EmptyState
                        icon={
                            <BarChart3 className="w-16 h-16" />
                        }
                        title={
                            activeTab === 'fast'
                                ? 'No fast movers found'
                                : activeTab === 'slow'
                                    ? 'No slow movers found'
                                    : 'No dead stock found'
                        }
                        description={
                            activeTab === 'dead'
                                ? 'All products have moved within the last 90 days. Great inventory management!'
                                : 'Not enough transaction data for this period. Try a longer time range.'
                        }
                    />
                ) : (
                    <Card className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead>
                                    <tr className="bg-slate-50/50">
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                            Product
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">
                                            Branch
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                            Stock In
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                            Stock Out
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                            Net Change
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase hidden lg:table-cell">
                                            Velocity
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">
                                            Last Moved
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-24">
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {paginatedEntries.map(
                                        (entry, idx) => (
                                            <motion.tr
                                                key={`${entry.productId}-${entry.branchName}`}
                                                initial={{
                                                    opacity: 0,
                                                    y: 10,
                                                }}
                                                animate={{
                                                    opacity: 1,
                                                    y: 0,
                                                }}
                                                transition={{
                                                    delay:
                                                        idx *
                                                        0.02,
                                                }}
                                                className={clsx(
                                                    'hover:bg-slate-50 transition-colors cursor-pointer',
                                                    entry.status ===
                                                    'dead' &&
                                                    'bg-rose-50/20',
                                                    entry.status ===
                                                    'slow' &&
                                                    'bg-amber-50/20'
                                                )}
                                                onClick={() =>
                                                    navigate(
                                                        `/inventory/levels/${entry.productId}`
                                                    )
                                                }
                                            >
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <Package
                                                            className={clsx(
                                                                'w-4 h-4',
                                                                entry.status ===
                                                                    'dead'
                                                                    ? 'text-rose-500'
                                                                    : entry.status ===
                                                                        'slow'
                                                                        ? 'text-amber-500'
                                                                        : entry.status ===
                                                                            'fast'
                                                                            ? 'text-blue-500'
                                                                            : 'text-slate-400'
                                                            )}
                                                        />
                                                        <span className="text-sm font-medium text-slate-700">
                                                            {
                                                                entry.productName
                                                            }
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-slate-500 hidden sm:table-cell">
                                                    {
                                                        entry.branchName
                                                    }
                                                </td>
                                                <td className="px-4 py-3 text-right text-sm text-emerald-600 font-semibold tabular-nums">
                                                    {entry.totalInflow >
                                                        0
                                                        ? formatNumber(
                                                            entry.totalInflow
                                                        )
                                                        : '—'}
                                                </td>
                                                <td className="px-4 py-3 text-right text-sm text-rose-600 font-semibold tabular-nums">
                                                    {entry.totalOutflow >
                                                        0
                                                        ? formatNumber(
                                                            entry.totalOutflow
                                                        )
                                                        : '—'}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <span
                                                        className={clsx(
                                                            'text-sm font-bold tabular-nums',
                                                            entry.netChange >
                                                                0
                                                                ? 'text-emerald-600'
                                                                : entry.netChange <
                                                                    0
                                                                    ? 'text-rose-600'
                                                                    : 'text-slate-400'
                                                        )}
                                                    >
                                                        {entry.netChange !==
                                                            0
                                                            ? `${entry.netChange > 0 ? '+' : ''}${formatNumber(entry.netChange)}`
                                                            : '0'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right text-sm text-slate-600 hidden lg:table-cell tabular-nums">
                                                    {entry.velocity >
                                                        0
                                                        ? `${entry.velocity.toFixed(2)}/day`
                                                        : '—'}
                                                </td>
                                                <td className="px-4 py-3 text-right text-sm text-slate-500 hidden md:table-cell">
                                                    {entry.daysSinceLastMovement >=
                                                        999 ? (
                                                        <span className="text-rose-500 font-semibold">
                                                            Never
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            {formatDate(
                                                                entry.lastMovementDate
                                                            )}
                                                            <span className="text-xs text-slate-400 ml-1">
                                                                (
                                                                {
                                                                    entry.daysSinceLastMovement
                                                                }
                                                                d)
                                                            </span>
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    {entry.status ===
                                                        'fast' ? (
                                                        <Badge
                                                            variant="info"
                                                            size="sm"
                                                        >
                                                            <Zap className="w-3 h-3 mr-1" />{' '}
                                                            Fast
                                                        </Badge>
                                                    ) : entry.status ===
                                                        'slow' ? (
                                                        <Badge
                                                            variant="warning"
                                                            size="sm"
                                                        >
                                                            <Snail className="w-3 h-3 mr-1" />{' '}
                                                            Slow
                                                        </Badge>
                                                    ) : (
                                                        <Badge
                                                            variant="danger"
                                                            size="sm"
                                                        >
                                                            <Skull className="w-3 h-3 mr-1" />{' '}
                                                            Dead
                                                        </Badge>
                                                    )}
                                                </td>
                                            </motion.tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {totalPages > 1 && (
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                totalItems={totalCount}
                                pageSize={PAGE_SIZE}
                                onPageChange={
                                    handlePageChange
                                }
                                onPageSizeChange={() => { }}
                            />
                        )}
                    </Card>
                )}
            </div>
        </div>
    );
}